import type { Extension } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import {
  Bot,
  Braces,
  Brackets,
  ChartColumn,
  Coffee,
  Container,
  Eye,
  FileCode2,
  Flame,
  FolderTree,
  History,
  ShieldAlert,
  SquareTerminal,
  Keyboard,
  ListIndentIncrease,
  ListTodo,
  Palette,
  Pause,
  Pilcrow,
  Play,
  Radio,
  Sparkles,
  SwatchBook,
  Timer,
  Users,
  WandSparkles,
  type LucideIcon,
} from "lucide-react";
import { useEffect, type ComponentType } from "react";
import { toast } from "sonner";
import { languageForPath, type PluginId } from "@ide/shared";
import { colorTheme, hackerTheme } from "@/editor/theme";
import { activeEditor } from "@/workspace/active-editor";
import { ClaudeMark, CopilotMark, GithubMark } from "@/components/icons";
import { useNow } from "@/lib/use-now";
import { useWorkspace } from "@/workspace/store";
import {
  colorPreview,
  gotoNextTodo,
  indentGuides,
  loadVim,
  prettierKeymap,
  rainbowBrackets,
  todoHighlighter,
  visibleWhitespace,
} from "./editor-extensions";
import { activePluginPath } from "./active-path";
import { AGENT_COMMANDS, AgentOverlay, agentKeymap, COPILOT_COMMANDS, copilotKeymap } from "./ai-plugins";
import { AuthorshipItem, MarkdownPreviewOverlay, openMarkdownPreview } from "./extra-plugins";
import { COPILOT_GITHUB_COMMANDS, CopilotOverlay, CopilotStatusItem } from "./github-copilot";
import { LIVE_SERVER_COMMANDS, LiveServerStatusItem } from "./live-server";
import { SIDEBAR_COLOR_COMMANDS, SidebarColorOverlay } from "./sidebar-color";
import { formatClock, useFocusTimer } from "./focus-timer";
import {
  DOCKER_PLUGIN,
  JAVA_PLUGIN,
  popularSnippets,
  PYTHON_PLUGIN,
  secretScanner,
} from "./language-plugins";
import { canFormat, formatDocument } from "./format";

export const PLUGIN_ICONS: Record<string, LucideIcon> = {
  bot: Bot,
  braces: Braces,
  coffee: Coffee,
  container: Container,
  eye: Eye,
  "folder-tree": FolderTree,
  history: History,
  python: FileCode2,
  shield: ShieldAlert,
  terminal: SquareTerminal,
  wand: WandSparkles,
  github: GithubMark as unknown as LucideIcon,
  claude: ClaudeMark as unknown as LucideIcon,
  copilot: CopilotMark as unknown as LucideIcon,
  radio: Radio,
  palette: Palette,
  flame: Flame,
  keyboard: Keyboard,
  indent: ListIndentIncrease,
  brackets: Brackets,
  "list-todo": ListTodo,
  swatch: SwatchBook,
  pilcrow: Pilcrow,
  sparkles: Sparkles,
  timer: Timer,
  chart: ChartColumn,
  users: Users,
};

export interface PluginCommand {
  id: string;
  title: string;
  shortcut?: string;
  run: () => void | Promise<void>;
}

/** O que um plugin entrega à interface. Cada campo corresponde a uma permissão do manifesto. */
export interface PluginRuntime {
  /** Extensões do CodeMirror (editor.decorate, editor.keymap, editor.edit). */
  editor?: () => Extension | Promise<Extension>;
  /** Substitui o tema do editor (editor.theme). */
  theme?: (dark: boolean) => Extension;
  /** Comandos na paleta (commands). */
  commands?: PluginCommand[];
  /** Item na barra de status (ui.statusbar). */
  StatusItem?: ComponentType;
  /** O item vai para o canto direito (uso das IAs, como no VS Code). */
  statusRight?: boolean;
  /** Classe na raiz do workspace (efeitos só de CSS). */
  rootClass?: string;
  /** Diálogos do plugin, montados no workspace (abertos pelos comandos). */
  Overlay?: ComponentType;
  /**
   * Classes aplicadas no <html> enquanto o plugin está ligado (ui.theme): trocam as variáveis de
   * cor da interface inteira. "theme-color-code" liga as cores dos sinais do código.
   */
  uiTheme?: string;
  /** Plugin "Tema claro": troca o escuro padrão pelo claro (interface e editor). */
  mode?: "light";
}

export { setActivePluginPath } from "./active-path";

/**
 * Formatar documento (Shift+Alt+F, menu Editar, botão do editor): Prettier nas linguagens que ele
 * conhece e a limpeza básica nas outras. Não precisa de plugin.
 */
export async function formatActive(view?: EditorView) {
  const target = view ?? activeEditor.current?.view;
  if (!target) return void toast("Abra um arquivo para formatar.");
  if (target.state.readOnly) return void toast("Seu papel não permite editar este arquivo.");
  const language = languageForPath(activePluginPath());
  try {
    const changed = await formatDocument(target, language);
    toast(changed ? "Arquivo formatado" : "Já estava formatado", {
      description: canFormat(language)
        ? "Prettier · edição mínima"
        : "Espaços no fim e linhas em branco extras",
    });
  } catch (err) {
    toast.error("Não deu para formatar", {
      description: err instanceof Error ? err.message.split("\n")[0] : "",
    });
  }
}

function FocusTimerItem() {
  const { running, remainingMs, phase, start, pause, tick } = useFocusTimer();
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => tick(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [running, tick]);
  return (
    <button
      onClick={running ? pause : start}
      className="flex items-center gap-1.5 hover:text-fg"
      title={running ? "Pausar o timer de foco" : "Iniciar o timer de foco"}
    >
      {running ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
      <span>
        {phase === "focus" ? "foco" : "pausa"} {formatClock(remainingMs)}
      </span>
    </button>
  );
}

function DocStatsItem() {
  // Reconta a cada edição (o cursor muda) e a cada segundo: o editor de uma aba nova é
  // criado depois da troca de aba, então só o gatilho do cursor deixaria o número velho.
  useWorkspace((s) => s.cursor);
  useNow(1_000);
  const doc = activeEditor.current?.view.state.doc;
  if (!doc) return null;
  const text = doc.toString();
  const words = text.match(/[\p{L}\p{N}_]+/gu)?.length ?? 0;
  return (
    <span>
      {doc.lines} ln · {words} palavras · {text.length} car.
    </span>
  );
}

export const PLUGIN_RUNTIME: Record<PluginId, PluginRuntime> = {
  "kompil.prettier": {
    editor: () => prettierKeymap((view) => void formatActive(view)),
    commands: [
      {
        id: "prettier.format",
        title: "Formatar documento (Prettier)",
        shortcut: "⇧⌥F",
        run: () => formatActive(),
      },
    ],
  },
  "kompil.theme-aurora": {
    theme: (dark) => colorTheme("aurora", dark),
    uiTheme: "theme-color-code ui-theme-aurora",
  },
  "kompil.theme-ember": {
    theme: (dark) => colorTheme("ember", dark),
    uiTheme: "theme-color-code ui-theme-ember",
  },
  "kompil.sidebar-color": { Overlay: SidebarColorOverlay, commands: SIDEBAR_COLOR_COMMANDS },
  "kompil.vim": { editor: loadVim },
  "kompil.indent-guides": { editor: () => indentGuides },
  "kompil.rainbow-brackets": { editor: () => rainbowBrackets },
  "kompil.todo-highlighter": {
    editor: () => todoHighlighter,
    commands: [
      {
        id: "todo.next",
        title: "Ir para o próximo TODO",
        run: () => {
          const view = activeEditor.current?.view;
          if (!view || !gotoNextTodo(view)) toast("Nenhum TODO neste arquivo.");
        },
      },
    ],
  },
  "kompil.color-preview": { editor: () => colorPreview },
  "kompil.whitespace": { editor: () => visibleWhitespace },
  "kompil.zen-mode": {
    commands: [
      {
        id: "zen.toggle",
        title: "Modo zen: alternar",
        run: () => {
          const ws = useWorkspace.getState();
          ws.setZen(!ws.zen);
        },
      },
    ],
  },
  "kompil.focus-timer": {
    StatusItem: FocusTimerItem,
    commands: [
      {
        id: "focus.toggle",
        title: "Timer de foco: iniciar/pausar",
        run: () => {
          const t = useFocusTimer.getState();
          if (t.running) t.pause();
          else t.start();
        },
      },
      { id: "focus.reset", title: "Timer de foco: zerar", run: () => useFocusTimer.getState().reset() },
    ],
  },
  "kompil.doc-stats": { StatusItem: DocStatsItem },
  "kompil.cursor-names": { rootClass: "plugin-cursor-names" },
  "kompil.claude-agent": {
    editor: agentKeymap,
    commands: AGENT_COMMANDS,
    // A conta e o uso do Claude ficam no painel Claude Code da barra da esquerda (não na de baixo).
    Overlay: AgentOverlay,
  },
  "kompil.copilot": { editor: copilotKeymap, commands: COPILOT_COMMANDS },
  // GitHub Copilot oficial: o editor usa o Copilot como fonte do texto fantasma (editor-pane).
  "kompil.github-copilot": {
    StatusItem: CopilotStatusItem,
    statusRight: true,
    Overlay: CopilotOverlay,
    commands: COPILOT_GITHUB_COMMANDS,
  },
  "kompil.live-server": { StatusItem: LiveServerStatusItem, commands: LIVE_SERVER_COMMANDS },
  "kompil.docker": DOCKER_PLUGIN,
  "kompil.java": JAVA_PLUGIN,
  "kompil.python": PYTHON_PLUGIN,
  "kompil.secret-scanner": { editor: () => secretScanner },
  "kompil.authorship": {
    StatusItem: AuthorshipItem,
    commands: [
      {
        id: "authorship.history",
        title: "Autoria: ver o histórico do projeto",
        run: () => useWorkspace.getState().setPanel("history"),
      },
    ],
  },
  "kompil.file-icons": {},
  "kompil.snippets": { editor: popularSnippets },
  "kompil.markdown-preview": {
    Overlay: MarkdownPreviewOverlay,
    commands: [
      {
        id: "markdown.preview",
        title: "Markdown: abrir prévia",
        run: () => {
          const problem = openMarkdownPreview();
          if (problem) toast(problem);
        },
      },
    ],
  },
  // Só o código fica verde: a interface da IDE não muda (sem uiTheme).
  "kompil.theme-light": { mode: "light" },
  "kompil.theme-hacker": {
    theme: (dark) => hackerTheme(dark),
  },
  "kompil.theme-colorido": { theme: (dark) => colorTheme("kompil", dark), uiTheme: "theme-color-code" },
  "kompil.theme-dracula": {
    theme: (dark) => colorTheme("dracula", dark),
    uiTheme: "theme-color-code ui-theme-dracula",
  },
  "kompil.theme-one-dark": {
    theme: (dark) => colorTheme("one-dark", dark),
    uiTheme: "theme-color-code ui-theme-one-dark",
  },
  "kompil.theme-github": {
    theme: (dark) => colorTheme("github", dark),
    uiTheme: "theme-color-code ui-theme-github",
  },
  "kompil.theme-nord": {
    theme: (dark) => colorTheme("nord", dark),
    uiTheme: "theme-color-code ui-theme-nord",
  },
  "kompil.theme-monokai": {
    theme: (dark) => colorTheme("monokai", dark),
    uiTheme: "theme-color-code ui-theme-monokai",
  },
};
